<template>
  <div>
    <v-autocomplete
      :id="id"
      outlined
      :value="value"
      @input="emitInput"
      :items="resolvedRecursos"
      :item-text="itemText"
      :item-value="itemValue"
      :filter="resolvedFilter"
      :search-input.sync="searchInput"
      :label="translatedLabel"
      :return-object="returnObject"
      :loading="loadingCampoRelacionamento"
      :disabled="disabled || (!remoteSearch && loadingCampoRelacionamento)"
      :clearable="clearable"
      :no-data-text="resolvedNoDataText"
      @focus="onFocus"
      class="
        input-style
        font-size-input
        text-light-input
        placeholder-light
        input-icon
      "
      dense
      flat
      filled
      solo
    >
      <template v-if="remoteSearch" v-slot:no-data>
        <v-list-item>
          <v-list-item-content>
            <div
              v-if="estaPesquisandoRemotamente"
              class="d-flex align-center py-2"
            >
              <v-progress-circular
                indeterminate
                size="18"
                width="2"
                color="primary"
                class="mr-3"
              />
              <span class="text-caption text--secondary"> Pesquisando... </span>
            </div>
            <v-list-item-title v-else class="text-caption text--secondary">
              {{ resolvedNoDataText }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template v-if="estaPesquisandoRemotamente" v-slot:append-item>
        <v-divider class="my-1" />
        <v-list-item dense>
          <v-list-item-content>
            <div class="d-flex align-center py-1">
              <v-progress-circular
                indeterminate
                size="16"
                width="2"
                color="primary"
                class="mr-3"
              />
              <span class="text-caption text--secondary"> Pesquisando... </span>
            </div>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template v-slot:append-outer>
        <v-btn
          v-if="showCadastroRapidoButton"
          v-can="resolvedInsertPermission"
          icon
          :title="$t('novo_registro')"
          :aria-label="$t('novo_registro')"
          :loading="loadingCadastroComponent"
          @click="openCadastroModal"
        >
          <v-icon color="primary" size="25">mdi-plus-circle</v-icon>
        </v-btn>
        <v-btn
          v-if="showEditarRapidoButton"
          v-can="resolvedUpdatePermission"
          icon
          class="ml-1"
          :title="$t('editar')"
          :aria-label="$t('editar')"
          :loading="loadingCadastroComponent"
          @click="openEditarModal"
        >
          <v-icon color="primary" size="23">mdi-pencil-circle</v-icon>
        </v-btn>
        <v-btn
          v-if="showListagemButton"
          icon
          class="ml-1"
          :disabled="disabled"
          :title="$t('Visualizar_e_selecionar')"
          :aria-label="$t('Visualizar_e_selecionar')"
          @click="openListagemModal"
        >
          <v-icon color="primary" size="23">mdi-format-list-bulleted</v-icon>
        </v-btn>
      </template>
    </v-autocomplete>

    <v-dialog
      v-model="dialogListagem"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="760"
      scrollable
    >
      <v-card>
        <v-card-title class="listagem-title py-3">
          <div class="d-flex flex-column">
            <span class="text-h6 font-weight-bold">
              {{ $t("Selecionar_registro") }} — {{ translatedLabel }}
            </span>
            <span class="text-caption font-weight-regular text--secondary">
              {{ $t("Clique_em_um_registro_para_selecionar") }}
            </span>
          </div>
          <v-spacer />
          <v-btn
            icon
            :title="$t('Fechar')"
            :aria-label="$t('Fechar')"
            @click="closeListagemModal"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="listagem-content pt-4">
          <v-text-field
            v-model="listagemSearchInput"
            :label="$t('Procurar')"
            :placeholder="$t('Digite_para_filtrar_a_listagem')"
            :loading="listagemLoading"
            clearable
            dense
            outlined
            hide-details
            class="mb-4"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#adb5bd" size="18">mdi-magnify</v-icon>
            </template>
          </v-text-field>

          <v-alert v-if="listagemError" type="error" text dense class="mb-0">
            <div class="d-flex align-center">
              <span>{{ $t("Nao_foi_possivel_carregar_a_listagem") }}</span>
              <v-spacer />
              <v-btn text small color="error" @click="retryListagem">
                {{ $t("Tente_novamente") }}
              </v-btn>
            </div>
          </v-alert>

          <div
            v-else-if="listagemLoading"
            class="d-flex flex-column align-center justify-center py-10"
          >
            <v-progress-circular indeterminate color="primary" size="32" />
            <span class="text-caption text--secondary mt-3">
              {{ $t("Carregando_registros") }}
            </span>
          </div>

          <div
            v-else-if="listagemItems.length === 0"
            class="text-center text--secondary py-10"
          >
            <v-icon color="grey lighten-1" size="40">
              mdi-database-search-outline
            </v-icon>
            <div class="text-body-2 mt-2">
              {{ $t("Nenhum_registro_encontrado") }}
            </div>
          </div>

          <v-list v-else class="listagem-list py-0" two-line>
            <v-list-item
              v-for="(item, index) in listagemItems"
              :key="getListItemKey(item, index)"
              :class="{
                'listagem-item--selected': isListItemSelected(item),
              }"
              class="listagem-item px-3"
              @click="selecionarRecursoListagem(item)"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ getItemDisplayText(item) }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="isListItemSelected(item)">
                  {{ $t("Registro_selecionado_atualmente") }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon
                  :color="
                    isListItemSelected(item) ? 'primary' : 'grey lighten-1'
                  "
                >
                  {{
                    isListItemSelected(item)
                      ? "mdi-check-circle"
                      : "mdi-chevron-right"
                  }}
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider />

        <v-card-actions class="listagem-footer px-4 py-3">
          <span
            v-if="!listagemLoading && !listagemError"
            class="text-caption text--secondary"
          >
            {{ listagemTotalLabel }}
          </span>
          <v-spacer />
          <v-pagination
            v-if="listagemTotalPages > 1"
            :value="listagemPage + 1"
            :length="listagemTotalPages"
            :disabled="listagemLoading"
            :total-visible="$vuetify.breakpoint.xsOnly ? 4 : 7"
            circle
            color="primary"
            @input="onListagemPageChange"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogCadastro"
      persistent
      max-width="100%"
      @keydown.esc.native.stop.prevent="onEscCadastroRapido"
    >
      <component
        :is="insertComponent"
        v-if="insertComponent"
        :itemAtual="recursoAtualModal"
        :recurso="recursoAtualModal"
        @submitSuccessInsert="onSubmitSuccessInsert"
        @submitErrorInsert="onSubmitErrorInsert"
        @closeModalInsert="closeCadastroModal"
        @submitSuccess="onSubmitSuccessEdit"
        @submitError="onSubmitErrorEdit"
        @closeModalEdit="closeCadastroModal"
      />
    </v-dialog>
  </div>
</template>

<script>
import { componentPaths } from "@/router/componentPaths";
import ComumDataService from "@/services/ComumDataService";
import { translateIfMessageExists } from "@/utils/i18n";

export default {
  name: "CampoRelacionamentoCadastroRapido",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      required: false,
      default: null,
    },
    url: {
      type: String,
      required: true,
    },
    cadastroUrl: {
      type: String,
      required: false,
      default: "",
    },
    label: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: false,
      default: null,
    },
    itemText: {
      type: [Function, String],
      required: false,
      default: "descricao",
    },
    itemValue: {
      type: String,
      required: false,
      default: "id",
    },
    items: {
      type: Array,
      required: false,
      default: null,
    },
    filter: {
      type: Function,
      required: false,
      default: null,
    },
    returnObject: {
      type: Boolean,
      required: false,
      default: true,
    },
    insertPermission: {
      type: String,
      required: false,
      default: "",
    },
    updatePermission: {
      type: String,
      required: false,
      default: "",
    },
    editarUrl: {
      type: String,
      required: false,
      default: "",
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    remoteSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchUrl: {
      type: String,
      required: false,
      default: "",
    },
    listUrl: {
      type: String,
      required: false,
      default: "",
    },
    listPageSize: {
      type: Number,
      required: false,
      default: 10,
    },
    searchParam: {
      type: String,
      required: false,
      default: "termo",
    },
    minSearchLength: {
      type: Number,
      required: false,
      default: 2,
    },
    remotePageSize: {
      type: Number,
      required: false,
      default: 20,
    },
    remoteOrderBy: {
      type: String,
      required: false,
      default: "descricao",
    },
    remoteDirection: {
      type: String,
      required: false,
      default: "ASC",
    },
    remoteDebounceMs: {
      type: Number,
      required: false,
      default: 350,
    },
    remoteExtraParams: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    remoteCache: {
      type: Object,
      required: false,
      default: null,
    },
    initialSearch: {
      type: String,
      required: false,
      default: "",
    },
    loadOnFocus: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      recursos: [],
      loadingRecursos: false,
      buscaRemotaPendente: false,
      searchInput: "",
      searchDebounceTimer: null,
      remoteRequestId: 0,
      initialLoadFrame: null,
      initialLoadTimer: null,
      initialLoadTimerType: null,
      initialLoadPending: false,

      dialogListagem: false,
      listagemItems: [],
      listagemSearchInput: "",
      listagemPage: 0,
      listagemTotalPages: 0,
      listagemTotalElements: 0,
      listagemLoading: false,
      listagemError: false,
      listagemDebounceTimer: null,
      listagemRequestId: 0,

      dialogCadastro: false,
      insertComponent: null,
      loadingCadastroComponent: false,
      idsAntesCadastro: new Set(),
      recursoAtualModal: null,
      escListenerRegistered: false,
    };
  },

  mounted() {
    this.recursos = this.mergeSelectedRecurso(this.recursos);

    if (this.useExternalItems) {
      this.recursos = this.items || [];
      return;
    }

    if (this.remoteSearch) {
      return;
    }

    this.scheduleInitialRecursosLoad();
  },

  beforeDestroy() {
    this.cancelInitialRecursosLoad();
    this.remoteRequestId += 1;
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    this.cancelListagemRequests();
    this.buscaRemotaPendente = false;
    this.unregisterEscListener();
  },

  computed: {
    translatedLabel() {
      return translateIfMessageExists(this.$i18n, this.label);
    },

    useExternalItems() {
      return Array.isArray(this.items);
    },

    resolvedRecursos() {
      if (this.useExternalItems) {
        return this.mergeSelectedRecurso(this.items || []);
      }

      return this.mergeSelectedRecurso(this.recursos || []);
    },

    resolvedInsertPermission() {
      if (this.insertPermission) {
        return this.insertPermission;
      }

      if (!this.cadastroUrl || this.cadastroUrl.indexOf("cadastro.") !== 0) {
        return "";
      }

      const nomeSingular = this.cadastroUrl.replace("cadastro.", "");
      if (!nomeSingular) {
        return "";
      }

      return `ROLE_${nomeSingular}_Insert`;
    },

    showCadastroRapidoButton() {
      return !!(this.cadastroUrl && this.resolvedInsertPermission);
    },

    resolvedEditarUrl() {
      if (this.editarUrl) {
        return this.editarUrl;
      }

      if (!this.cadastroUrl || this.cadastroUrl.indexOf("cadastro.") !== 0) {
        return "";
      }

      const nomeSingular = this.cadastroUrl.replace("cadastro.", "");
      if (!nomeSingular) {
        return "";
      }

      const candidatos = [
        `editar.${nomeSingular}`,
        `editar.${nomeSingular.charAt(0).toLowerCase()}${nomeSingular.slice(
          1
        )}`,
        `editar.${nomeSingular.toLowerCase()}`,
      ];

      const urlEncontrada = candidatos.find((url) => componentPaths[url]);
      return urlEncontrada || "";
    },

    resolvedUpdatePermission() {
      if (this.updatePermission) {
        return this.updatePermission;
      }

      if (!this.cadastroUrl || this.cadastroUrl.indexOf("cadastro.") !== 0) {
        return "";
      }

      const nomeSingular = this.cadastroUrl.replace("cadastro.", "");
      if (!nomeSingular) {
        return "";
      }

      return `ROLE_${nomeSingular}_Update`;
    },

    hasSelectedRecurso() {
      const idSelecionado = this.getSelectedRecursoId();
      return idSelecionado !== undefined && idSelecionado !== null;
    },

    hasEditarComponent() {
      return !!componentPaths[this.resolvedEditarUrl];
    },

    showEditarRapidoButton() {
      return !!(
        this.hasSelectedRecurso &&
        this.resolvedEditarUrl &&
        this.resolvedUpdatePermission &&
        this.hasEditarComponent
      );
    },

    resolvedListUrl() {
      return this.listUrl || this.resolvedSearchUrl;
    },

    showListagemButton() {
      return !!(this.remoteSearch && this.resolvedListUrl);
    },

    resolvedListPageSize() {
      return Math.min(Math.max(Number(this.listPageSize) || 10, 1), 50);
    },

    resolvedFilter() {
      if (this.remoteSearch) {
        return () => true;
      }

      if (typeof this.filter === "function") {
        return this.filter;
      }

      return this.defaultFilter;
    },

    resolvedSearchUrl() {
      return this.searchUrl || this.url;
    },

    loadingCampoRelacionamento() {
      return this.loadingRecursos || this.buscaRemotaPendente;
    },

    estaPesquisandoRemotamente() {
      return this.remoteSearch && this.loadingCampoRelacionamento;
    },

    normalizedSearchInput() {
      return String(this.searchInput || "").trim();
    },

    resolvedNoDataText() {
      if (!this.remoteSearch) {
        return undefined;
      }

      if (this.normalizedSearchInput.length < this.minSearchLength) {
        return `Digite ao menos ${this.minSearchLength} caracteres para pesquisar`;
      }

      if (this.loadingRecursos) {
        return "Pesquisando...";
      }

      return "Nenhum registro encontrado";
    },

    listagemTotalLabel() {
      const total = Number(this.listagemTotalElements) || 0;
      return total === 1
        ? this.$t("Um_registro_encontrado")
        : this.$t("Registros_encontrados", { total });
    },
  },

  watch: {
    url() {
      if (this.useExternalItems) {
        return;
      }
      this.getRecursos();
    },
    value: {
      immediate: false,
      handler() {
        if (this.useExternalItems) {
          return;
        }
        this.recursos = this.mergeSelectedRecurso(this.recursos);
      },
    },
    searchInput(newValue) {
      if (!this.remoteSearch) {
        return;
      }
      this.agendarBuscaRemota(newValue);
    },
    listagemSearchInput(newValue) {
      if (!this.dialogListagem) {
        return;
      }
      this.agendarBuscaListagem(newValue);
    },
    items(newValue) {
      if (!this.useExternalItems) {
        return;
      }

      this.recursos = newValue || [];
    },
    dialogListagem(newValue) {
      if (!newValue) {
        this.cancelListagemRequests();
      }
    },
    dialogCadastro(newValue) {
      if (newValue) {
        this.registerEscListener();
      } else {
        this.unregisterEscListener();
      }
    },
  },

  methods: {
    cancelListagemRequests() {
      if (this.listagemDebounceTimer) {
        clearTimeout(this.listagemDebounceTimer);
        this.listagemDebounceTimer = null;
      }
      this.listagemRequestId += 1;
      this.listagemLoading = false;
    },

    cancelInitialRecursosLoad() {
      if (this.initialLoadFrame !== null) {
        if (
          typeof window !== "undefined" &&
          typeof window.cancelAnimationFrame === "function"
        ) {
          window.cancelAnimationFrame(this.initialLoadFrame);
        }
        this.initialLoadFrame = null;
      }
      if (this.initialLoadTimer !== null) {
        if (
          this.initialLoadTimerType === "idle" &&
          typeof window !== "undefined" &&
          typeof window.cancelIdleCallback === "function"
        ) {
          window.cancelIdleCallback(this.initialLoadTimer);
        } else {
          clearTimeout(this.initialLoadTimer);
        }
        this.initialLoadTimer = null;
        this.initialLoadTimerType = null;
      }
      this.initialLoadPending = false;
    },

    scheduleInitialRecursosLoad() {
      if (
        this.initialLoadPending ||
        this.loadingRecursos ||
        this.recursos.length > 0
      ) {
        return;
      }

      this.initialLoadPending = true;
      const load = () => {
        this.initialLoadFrame = null;
        this.initialLoadTimer = null;
        this.initialLoadTimerType = null;
        this.initialLoadPending = false;
        this.getRecursos();
      };

      this.initialLoadTimerType = "timeout";
      this.initialLoadTimer = setTimeout(() => {
        this.initialLoadTimer = null;
        this.initialLoadTimerType = null;

        if (
          typeof window !== "undefined" &&
          typeof window.requestIdleCallback === "function"
        ) {
          this.initialLoadTimerType = "idle";
          this.initialLoadTimer = window.requestIdleCallback(load, {
            timeout: 1200,
          });
          return;
        }

        load();
      }, 450);
    },

    normalizeSearchValue(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    },

    getValueByPath(item, path) {
      if (!item || !path) {
        return "";
      }

      return path.split(".").reduce((acc, key) => {
        if (acc === null || acc === undefined) {
          return "";
        }
        return acc[key];
      }, item);
    },

    getSearchableTextFromItem(item) {
      if (!item) {
        return "";
      }

      if (typeof this.itemText === "function") {
        return this.itemText(item);
      }

      if (typeof this.itemText === "string" && this.itemText.length > 0) {
        return this.getValueByPath(item, this.itemText);
      }

      return [
        item.descricao,
        item.nome,
        item.identificador,
        item.fantasia,
        item.razaoSocial,
        item.nRegistro,
      ]
        .filter((text) => text !== undefined && text !== null)
        .join(" ");
    },

    getItemDisplayText(item) {
      if (!item) {
        return "";
      }

      let text = "";
      try {
        text =
          typeof this.itemText === "function"
            ? this.itemText(item)
            : this.getValueByPath(item, this.itemText);
      } catch (error) {
        text = "";
      }

      if (text !== undefined && text !== null && String(text).trim()) {
        return String(text);
      }

      return String(
        item.descricao ||
          item.nome ||
          item.fantasia ||
          item.razaoSocial ||
          item.identificador ||
          item.nRegistro ||
          this.getItemId(item) ||
          ""
      );
    },

    defaultFilter(item, queryText, itemText) {
      const normalizedQuery = this.normalizeSearchValue(queryText);
      if (!normalizedQuery) {
        return true;
      }

      const normalizedItemText = this.normalizeSearchValue(itemText);
      if (normalizedItemText.includes(normalizedQuery)) {
        return true;
      }

      const fallbackText = this.normalizeSearchValue(
        this.getSearchableTextFromItem(item)
      );
      return fallbackText.includes(normalizedQuery);
    },

    emitInput(newValue) {
      this.$emit("input", newValue);
      this.$emit("change", newValue);
    },

    areSameId(firstId, secondId) {
      if (firstId === undefined || firstId === null) {
        return false;
      }
      if (secondId === undefined || secondId === null) {
        return false;
      }

      return String(firstId) === String(secondId);
    },

    getItemId(item) {
      if (!item || typeof item !== "object") {
        return null;
      }

      if (item[this.itemValue] !== undefined && item[this.itemValue] !== null) {
        return item[this.itemValue];
      }

      if (item.id !== undefined && item.id !== null) {
        return item.id;
      }

      return null;
    },

    getSelectedRecursoId() {
      if (this.returnObject) {
        return this.getItemId(this.value);
      }

      if (this.value === undefined || this.value === null) {
        return null;
      }

      return this.value;
    },

    findRecursoById(id) {
      if (id === undefined || id === null) {
        return null;
      }

      return (
        (this.resolvedRecursos || []).find((item) => {
          return this.areSameId(this.getItemId(item), id);
        }) || null
      );
    },

    mergeSelectedRecurso(items) {
      const lista = Array.isArray(items) ? items.slice() : [];
      const recursoSelecionado =
        this.returnObject && this.value && typeof this.value === "object"
          ? this.value
          : null;

      if (!recursoSelecionado) {
        return lista;
      }

      const idSelecionado = this.getItemId(recursoSelecionado);
      if (idSelecionado === undefined || idSelecionado === null) {
        return lista;
      }

      const jaExiste = lista.some((item) =>
        this.areSameId(this.getItemId(item), idSelecionado)
      );
      if (!jaExiste) {
        lista.unshift(recursoSelecionado);
      }

      return lista;
    },

    getRecursoSelecionado() {
      const idSelecionado = this.getSelectedRecursoId();

      if (idSelecionado === undefined || idSelecionado === null) {
        return null;
      }

      const recursoDaLista = this.findRecursoById(idSelecionado);
      if (recursoDaLista) {
        return recursoDaLista;
      }

      if (this.returnObject && this.value && typeof this.value === "object") {
        return this.value;
      }

      return null;
    },

    buildResourceByIdUrl(id) {
      if (!this.url || id === undefined || id === null) {
        return "";
      }

      const baseUrl = String(this.url).split("?")[0].replace(/\/+$/, "");
      if (!baseUrl) {
        return "";
      }

      return `${baseUrl}/${id}`;
    },

    async getRecursoSelecionadoCompleto() {
      const recursoSelecionado = this.getRecursoSelecionado();
      const idSelecionado =
        this.getItemId(recursoSelecionado) || this.getSelectedRecursoId();

      if (idSelecionado === undefined || idSelecionado === null) {
        return recursoSelecionado;
      }

      const recursoUrl = this.buildResourceByIdUrl(idSelecionado);
      if (!recursoUrl) {
        return recursoSelecionado;
      }

      try {
        const response = await ComumDataService.get(recursoUrl);
        return response.data || recursoSelecionado;
      } catch (error) {
        console.log(error, "error");
        return recursoSelecionado;
      }
    },

    cloneValue(value) {
      if (!value || typeof value !== "object") {
        return value;
      }

      if (typeof structuredClone === "function") {
        try {
          return structuredClone(value);
        } catch (error) {
          // fallback abaixo
        }
      }

      try {
        return JSON.parse(JSON.stringify(value));
      } catch (error) {
        return { ...value };
      }
    },

    getRecursos(force = false) {
      if (!this.url) {
        return Promise.resolve();
      }

      if (this.useExternalItems && !force) {
        this.recursos = this.items || [];
        return Promise.resolve();
      }

      if (this.remoteSearch) {
        return this.buscarRecursosRemotos(this.searchInput, true);
      }

      this.loadingRecursos = true;
      return ComumDataService.getRecursos(this.url)
        .then((response) => {
          this.recursos = this.mergeSelectedRecurso(response.data || []);
        })
        .catch((error) => {
          console.log(error, "error");
        })
        .finally(() => {
          this.loadingRecursos = false;
        });
    },

    agendarBuscaRemota(value) {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const termo = String(value || "").trim();
      const selecionado = this.returnObject
        ? this.value
        : this.findRecursoById(this.value);
      const textoSelecionado = selecionado
        ? String(this.getSearchableTextFromItem(selecionado) || "").trim()
        : "";
      // O autocomplete também atualiza a busca ao exibir uma seleção (ex.: CEP).
      if (
        termo.length < this.minSearchLength ||
        (textoSelecionado && termo === textoSelecionado)
      ) {
        this.remoteRequestId += 1;
        this.recursos = this.mergeSelectedRecurso(
          selecionado ? [selecionado] : []
        );
        this.loadingRecursos = false;
        this.buscaRemotaPendente = false;
        return;
      }

      this.buscaRemotaPendente = true;
      this.remoteRequestId += 1;
      this.recursos = this.mergeSelectedRecurso([]);
      this.searchDebounceTimer = setTimeout(() => {
        this.buscaRemotaPendente = false;
        this.buscarRecursosRemotos(termo);
      }, this.remoteDebounceMs);
    },

    extrairListaRespostaRemota(data) {
      if (Array.isArray(data)) {
        return data;
      }

      if (Array.isArray(data?.content)) {
        return data.content;
      }

      if (Array.isArray(data?.items)) {
        return data.items;
      }

      if (Array.isArray(data?.data)) {
        return data.data;
      }

      return [];
    },

    extrairPaginacaoRespostaRemota(data, requestedPage = 0) {
      const items = this.extrairListaRespostaRemota(data);
      if (Array.isArray(data)) {
        return {
          items,
          page: 0,
          totalPages: items.length > 0 ? 1 : 0,
          totalElements: items.length,
        };
      }

      const totalElements = Number(
        data?.totalElements ?? data?.total ?? data?.count ?? items.length
      );
      const resolvedPageSize = this.resolvedListPageSize;
      const totalPages = Number(
        data?.totalPages ??
          data?.pageCount ??
          (totalElements > 0 ? Math.ceil(totalElements / resolvedPageSize) : 0)
      );

      return {
        items,
        page: Number(data?.number ?? data?.page ?? requestedPage) || 0,
        totalPages: Math.max(totalPages, 0),
        totalElements: Math.max(totalElements, 0),
      };
    },

    getListItemKey(item, index) {
      const id = this.getItemId(item);
      if (id !== undefined && id !== null) {
        return `listagem-${id}`;
      }
      return `listagem-${this.listagemPage}-${index}`;
    },

    isListItemSelected(item) {
      return this.areSameId(this.getItemId(item), this.getSelectedRecursoId());
    },

    openListagemModal() {
      if (!this.showListagemButton || this.disabled) {
        return;
      }

      this.cancelListagemRequests();
      this.listagemSearchInput = "";
      this.listagemItems = [];
      this.listagemPage = 0;
      this.listagemTotalPages = 0;
      this.listagemTotalElements = 0;
      this.listagemError = false;
      this.dialogListagem = true;
      this.buscarListagemRemota(0);
    },

    closeListagemModal() {
      this.dialogListagem = false;
      this.cancelListagemRequests();
    },

    agendarBuscaListagem() {
      if (this.listagemDebounceTimer) {
        clearTimeout(this.listagemDebounceTimer);
      }

      this.listagemRequestId += 1;
      this.listagemLoading = true;
      this.listagemError = false;
      this.listagemPage = 0;
      this.listagemDebounceTimer = setTimeout(() => {
        this.listagemDebounceTimer = null;
        this.buscarListagemRemota(0);
      }, this.remoteDebounceMs);
    },

    buscarListagemRemota(page = 0) {
      if (!this.resolvedListUrl) {
        this.listagemLoading = false;
        this.listagemError = true;
        return Promise.resolve();
      }

      const params = {
        ...this.remoteExtraParams,
        [this.searchParam]: String(this.listagemSearchInput || "").trim(),
        page: Math.max(Number(page) || 0, 0),
        linesPerPage: this.resolvedListPageSize,
        orderBy: this.remoteOrderBy,
        direction: this.remoteDirection,
      };
      const requestId = ++this.listagemRequestId;

      this.listagemLoading = true;
      this.listagemError = false;
      return ComumDataService.get(this.resolvedListUrl, { params })
        .then((response) => {
          if (requestId !== this.listagemRequestId) {
            return;
          }

          const pagination = this.extrairPaginacaoRespostaRemota(
            response.data,
            params.page
          );
          this.listagemItems = pagination.items;
          this.listagemPage = pagination.page;
          this.listagemTotalPages = pagination.totalPages;
          this.listagemTotalElements = pagination.totalElements;
        })
        .catch((error) => {
          if (requestId !== this.listagemRequestId) {
            return;
          }
          console.log(error, "error");
          this.listagemItems = [];
          this.listagemTotalPages = 0;
          this.listagemTotalElements = 0;
          this.listagemError = true;
        })
        .finally(() => {
          if (requestId === this.listagemRequestId) {
            this.listagemLoading = false;
          }
        });
    },

    retryListagem() {
      return this.buscarListagemRemota(this.listagemPage);
    },

    onListagemPageChange(value) {
      const page = Math.max(Number(value) - 1, 0);
      if (page === this.listagemPage || this.listagemLoading) {
        return;
      }
      this.buscarListagemRemota(page);
    },

    selecionarRecursoListagem(item) {
      if (!item || this.disabled) {
        return;
      }

      if (!this.useExternalItems) {
        const id = this.getItemId(item);
        const jaExiste = (this.recursos || []).some((recurso) =>
          this.areSameId(this.getItemId(recurso), id)
        );
        if (!jaExiste) {
          this.recursos = [item, ...(this.recursos || [])];
        }
      }

      this.emitInput(this.getModelValueFromItem(item));
      this.$emit("selectFromList", item);
      this.closeListagemModal();
    },

    getRemoteCacheKey(params) {
      if (!this.remoteCache || !this.resolvedSearchUrl) {
        return "";
      }

      const normalizedParams = Object.keys(params || {})
        .sort()
        .reduce((acc, key) => {
          const value = params[key];
          if (value !== undefined && value !== null) {
            acc[key] = value;
          }
          return acc;
        }, {});

      return [
        "CampoRelacionamentoCadastroRapido",
        this.resolvedSearchUrl,
        JSON.stringify(normalizedParams),
      ].join("|");
    },

    getCachedRemoteItems(cacheKey) {
      if (!this.remoteCache || !cacheKey) {
        return undefined;
      }

      if (!Object.prototype.hasOwnProperty.call(this.remoteCache, cacheKey)) {
        return undefined;
      }

      const cached = this.remoteCache[cacheKey];
      if (!cached || !Array.isArray(cached.items)) {
        return [];
      }

      return cached.items.slice();
    },

    setCachedRemoteItems(cacheKey, items) {
      if (!this.remoteCache || !cacheKey) {
        return;
      }

      this.$set(this.remoteCache, cacheKey, {
        items: Array.isArray(items) ? items.slice() : [],
        createdAt: Date.now(),
      });
    },

    buscarRecursosRemotos(value, force = false, options = {}) {
      const termo = String(value || "").trim();
      if (!force && termo.length < this.minSearchLength) {
        this.remoteRequestId += 1;
        this.recursos = this.mergeSelectedRecurso([]);
        this.loadingRecursos = false;
        this.buscaRemotaPendente = false;
        return Promise.resolve();
      }

      if (!this.resolvedSearchUrl) {
        this.loadingRecursos = false;
        this.buscaRemotaPendente = false;
        return Promise.resolve();
      }

      const params = {
        ...this.remoteExtraParams,
        [this.searchParam]: termo,
        page: 0,
        linesPerPage: this.remotePageSize,
        orderBy: this.remoteOrderBy,
        direction: this.remoteDirection,
      };

      const requestId = ++this.remoteRequestId;
      const cacheKey = this.getRemoteCacheKey(params);
      const cachedItems = options.ignoreCache
        ? undefined
        : this.getCachedRemoteItems(cacheKey);

      if (cachedItems !== undefined) {
        this.buscaRemotaPendente = false;
        this.loadingRecursos = false;
        this.recursos = this.mergeSelectedRecurso(cachedItems);
        return Promise.resolve();
      }

      this.buscaRemotaPendente = false;
      this.loadingRecursos = true;
      return ComumDataService.get(this.resolvedSearchUrl, { params })
        .then((response) => {
          if (requestId !== this.remoteRequestId) {
            return;
          }

          const items = this.extrairListaRespostaRemota(response.data);
          this.setCachedRemoteItems(cacheKey, items);
          this.recursos = this.mergeSelectedRecurso(items);
        })
        .catch((error) => {
          if (requestId !== this.remoteRequestId) {
            return;
          }
          console.log(error, "error");
          this.recursos = this.mergeSelectedRecurso([]);
        })
        .finally(() => {
          if (requestId === this.remoteRequestId) {
            this.loadingRecursos = false;
          }
        });
    },

    onFocus() {
      this.$emit("focus");

      if (
        this.loadOnFocus &&
        !this.remoteSearch &&
        !this.useExternalItems &&
        !this.loadingRecursos &&
        !this.initialLoadPending &&
        this.recursos.length === 0
      ) {
        this.getRecursos();
      }

      if (!this.remoteSearch || this.normalizedSearchInput) {
        return;
      }

      const termoInicial = String(this.initialSearch || "").trim();
      if (termoInicial.length < this.minSearchLength) {
        return;
      }

      this.searchInput = termoInicial;
    },

    async atualizarRecursosAposCadastro(formData) {
      if (!this.remoteSearch) {
        await this.getRecursos(this.useExternalItems);
        return;
      }

      const termo =
        formData?.descricao ||
        formData?.identificador ||
        formData?.nome ||
        formData?.fantasia ||
        formData?.razaoSocial ||
        formData?.nRegistro ||
        "";
      await this.buscarRecursosRemotos(termo || this.searchInput, true, {
        ignoreCache: true,
      });
    },

    async loadCadastroComponent(componentUrl) {
      if (!componentUrl) {
        return;
      }

      this.loadingCadastroComponent = true;
      try {
        if (componentPaths[componentUrl]) {
          const componentModule = await componentPaths[componentUrl]();
          const component = componentModule.default || componentModule;

          if (!component || (!component.template && !component.render)) {
            this.insertComponent = {
              template: "<div>Componente não encontrado ou inválido</div>",
            };
          } else {
            this.insertComponent = component;
          }
        } else {
          this.insertComponent = {
            template: "<div>Componente não encontrado</div>",
          };
        }
      } catch (error) {
        console.log(error);
        this.insertComponent = {
          template: "<div>Erro ao carregar o componente</div>",
        };
      } finally {
        this.loadingCadastroComponent = false;
      }
    },

    async openCadastroModal() {
      this.idsAntesCadastro = new Set(
        (this.recursos || [])
          .map((item) => item && item.id)
          .filter((id) => id !== undefined && id !== null)
      );

      this.recursoAtualModal = null;
      this.dialogCadastro = true;
      await this.loadCadastroComponent(this.cadastroUrl);
    },

    async openEditarModal() {
      const recursoSelecionado = await this.getRecursoSelecionadoCompleto();
      if (!recursoSelecionado) {
        return;
      }

      this.recursoAtualModal = this.cloneValue(recursoSelecionado);
      this.dialogCadastro = true;
      await this.loadCadastroComponent(this.resolvedEditarUrl);
    },

    closeCadastroModal() {
      this.dialogCadastro = false;
      this.insertComponent = null;
      this.recursoAtualModal = null;
    },

    registerEscListener() {
      if (this.escListenerRegistered) {
        return;
      }

      window.addEventListener("keydown", this.handleEscCapture, true);
      this.escListenerRegistered = true;
    },

    unregisterEscListener() {
      if (!this.escListenerRegistered) {
        return;
      }

      window.removeEventListener("keydown", this.handleEscCapture, true);
      this.escListenerRegistered = false;
    },

    onEscCadastroRapido() {
      if (this.dialogCadastro) {
        this.closeCadastroModal();
      }
    },

    handleEscCapture(event) {
      if (!this.dialogCadastro) {
        return;
      }

      if (event.key !== "Escape" && event.key !== "Esc") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }

      this.closeCadastroModal();
    },

    getModelValueFromItem(item) {
      if (!item) {
        return null;
      }
      if (this.returnObject) {
        return item;
      }
      return item[this.itemValue];
    },

    getNovoRecursoPorDiferenca() {
      const novos = (this.recursos || []).filter((item) => {
        if (!item || item.id === undefined || item.id === null) {
          return false;
        }
        return !this.idsAntesCadastro.has(item.id);
      });

      if (novos.length === 1) {
        return novos[0];
      }

      if (novos.length > 1) {
        return novos
          .slice()
          .sort((a, b) => Number(b.id || 0) - Number(a.id || 0))[0];
      }

      return null;
    },

    getNovoRecursoPorFormData(formData) {
      if (!formData || typeof formData !== "object") {
        return null;
      }

      if (formData.id !== undefined && formData.id !== null) {
        const encontradoPorId = (this.recursos || []).find(
          (item) => item && item.id === formData.id
        );
        if (encontradoPorId) {
          return encontradoPorId;
        }
      }

      if (formData.descricao) {
        const encontradoPorDescricao = (this.recursos || []).find(
          (item) => item && item.descricao === formData.descricao
        );
        if (encontradoPorDescricao) {
          return encontradoPorDescricao;
        }
      }

      if (formData.identificador) {
        const encontradoPorIdentificador = (this.recursos || []).find(
          (item) => item && item.identificador === formData.identificador
        );
        if (encontradoPorIdentificador) {
          return encontradoPorIdentificador;
        }
      }

      if (formData.nRegistro) {
        const encontradoPorRegistro = (this.recursos || []).find(
          (item) => item && item.nRegistro === formData.nRegistro
        );
        if (encontradoPorRegistro) {
          return encontradoPorRegistro;
        }
      }

      if (formData.fantasia) {
        const encontradoPorFantasia = (this.recursos || []).find(
          (item) => item && item.fantasia === formData.fantasia
        );
        if (encontradoPorFantasia) {
          return encontradoPorFantasia;
        }
      }

      if (formData.razaoSocial) {
        const encontradoPorRazaoSocial = (this.recursos || []).find(
          (item) => item && item.razaoSocial === formData.razaoSocial
        );
        if (encontradoPorRazaoSocial) {
          return encontradoPorRazaoSocial;
        }
      }

      return null;
    },

    async onSubmitSuccessInsert(repeat, formData) {
      await this.atualizarRecursosAposCadastro(formData);

      const novoRecurso =
        this.getNovoRecursoPorDiferenca() ||
        this.getNovoRecursoPorFormData(formData);

      if (novoRecurso) {
        this.emitInput(this.getModelValueFromItem(novoRecurso));
      }

      this.$emit("submitSuccessInsert", repeat, formData, novoRecurso || null);
      this.closeCadastroModal();
    },

    onSubmitErrorInsert() {
      this.$emit("submitErrorInsert");
    },

    async onSubmitSuccessEdit(...args) {
      await this.getRecursos(this.useExternalItems);

      const idRecursoEditado =
        this.getItemId(this.recursoAtualModal) || this.getSelectedRecursoId();
      const recursoAtualizado = this.findRecursoById(idRecursoEditado);

      if (recursoAtualizado) {
        this.emitInput(this.getModelValueFromItem(recursoAtualizado));
      }

      this.$emit("submitSuccessEdit", ...args, recursoAtualizado || null);
      this.closeCadastroModal();
    },

    onSubmitErrorEdit() {
      this.$emit("submitErrorEdit");
    },
  },
};
</script>

<style scoped>
.listagem-title {
  min-height: 72px;
}

.listagem-content {
  min-height: 360px;
}

.listagem-list {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.listagem-item {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.listagem-item .v-list-item__title {
  line-height: 1.35;
  white-space: normal;
}

.listagem-item:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.listagem-item:hover,
.listagem-item:focus {
  background-color: rgba(25, 118, 210, 0.06);
}

.listagem-item--selected {
  background-color: rgba(25, 118, 210, 0.1);
}

.listagem-footer {
  min-height: 64px;
}

@media (max-width: 600px) {
  .listagem-content {
    min-height: 0;
  }

  .listagem-footer {
    align-items: center;
    flex-direction: column;
    gap: 8px;
  }
}
</style>
